/**
 * `app-config` — operational configuration for Melodimals, resolved in layers.
 *
 * Portfolio doctrine (`infra/CONFIG_OVER_CODE.md`): a magic number that
 * governs operation is an autonomy bug. But the canon kit assumes an
 * overrides table in Postgres and a back-office, and **Melodimals has
 * neither of those two things**: there are no accounts, there is no PowerSync,
 * and the game does not talk to any backend. Copying the kit as-is would have meant
 * spinning up a server the game does not use just to claim the recipe was followed.
 *
 * So there are TWO levels here, the ones that actually exist:
 *
 *   | Level | Who changes it | Where it lives |
 *   |---|---|---|
 *   | **Platform** | whoever builds the binary | `VITE_MELODIMALS_*` (baked into the bundle) |
 *   | **Adult** | the adult, behind the parental gate | `localStorage`, per device |
 *   | **Default** | the code | this file, in ONE place |
 *
 * Precedence: **platform > adult > default**, with two nuances that explain
 * why resolution is a pure function and not a handful of `??`:
 *
 *  1. **The platform clamps, it does not merely set.** Each key declares its range; an
 *     out-of-range value (env with a typo, manipulated `localStorage`, JSON from
 *     another version) is not accepted halfway: it is clamped to the range or falls back
 *     to the default. There is never half-applied config.
 *  2. **Ads can only be HARDENED.** The adult can request FEWER
 *     ads than the platform ceiling; never more. This is the same doctrine
 *     as the tier regime in `growth/config.ts` — the person behind the
 *     parental gate is an adult, but the person using the app is still a child.
 *
 * What has NOT been modeled, and why, is in `docs/CONFIG_INVENTORY.md`.
 */

/** Difficulty presets for the mini-games (what an adult chooses, not 7 numbers). */
export const DIFFICULTY_PRESETS = ["gentle", "normal", "challenge"] as const;

export type DifficultyPreset = (typeof DIFFICULTY_PRESETS)[number];

/** Thresholds for the three mini-games. The `game-core` reducers already receive them. */
export interface GamesConfig {
  /** Notes in Simon's starting sequence. */
  simonStartLength: number;
  /** Length that wins the game. */
  simonMaxLength: number;
  /** Duration of each demo note, in ms (listening rhythm). */
  simonDemoNoteMs: number;
  /** Rounds of "Who sounds?". */
  whoSoundsRounds: number;
  /** Choices per round, including the correct one. */
  whoSoundsChoices: number;
  /** Maximum number to count in "Count and tap". */
  countMax: number;
  /** Rounds of "Count and tap". */
  countRounds: number;
}

export interface NarrationConfig {
  /** Narrator voice. Turning it off leaves the whole game without voice (music only). */
  enabled: boolean;
}

export interface ParentalConfig {
  /** Minimum addend for the parental-gate challenge. */
  challengeMin: number;
  /** Addend range (the challenge is `min..min+span-1`). */
  challengeSpan: number;
  /**
   * Minutes the gate pass remains valid after it is passed. `0`
   * preserves the historical behavior: the pass expires on leaving /parents.
   */
  gatePassMinutes: number;
}

/** Ad capping (§4 of the strategy). Feeds the gate's `AdCapPolicy`. */
export interface AdCapsConfig {
  interstitialPerSession: number;
  interstitialPerDay: number;
  /** Minimum separation between interstitials, in usage minutes. */
  minGapMinutes: number;
}

export interface AppConfig {
  difficulty: DifficultyPreset;
  games: GamesConfig;
  narration: NarrationConfig;
  parental: ParentalConfig;
  adCaps: AdCapsConfig;
}

/** Settings the adult can change after the parental gate. */
export interface ParentSettings {
  difficulty?: DifficultyPreset;
  narrationEnabled?: boolean;
  gatePassMinutes?: number;
  /** Daily interstitial cap. Counts only if it is STRICTER than the platform cap. */
  interstitialPerDay?: number;
  /** Interstitial cap per session. Same criterion: it can only harden. */
  interstitialPerSession?: number;
}

/**
 * Thresholds for each preset. This is the table that translates an "adult decision" into
 * the seven numbers the reducers consume — and the only place where game
 * difficulty is changed.
 */
export const DIFFICULTY_TABLE: Readonly<Record<DifficultyPreset, GamesConfig>> = {
  // Ages 2-3: short sequences, slow demo, fewer choices and rounds.
  gentle: {
    simonStartLength: 1,
    simonMaxLength: 5,
    simonDemoNoteMs: 850,
    whoSoundsRounds: 4,
    whoSoundsChoices: 2,
    countMax: 5,
    countRounds: 4,
  },
  // Historical behavior, byte for byte: configure nothing and nothing changes.
  normal: {
    simonStartLength: 2,
    simonMaxLength: 8,
    simonDemoNoteMs: 650,
    whoSoundsRounds: 6,
    whoSoundsChoices: 3,
    countMax: 10,
    countRounds: 6,
  },
  // Ages 5-6 who are already bored by the above.
  challenge: {
    simonStartLength: 3,
    simonMaxLength: 12,
    simonDemoNoteMs: 500,
    whoSoundsRounds: 8,
    whoSoundsChoices: 4,
    countMax: 10,
    countRounds: 8,
  },
};

/**
 * Allowed range for each numeric key. Out of range is not "approximated": it
 * is clamped. A `VITE_MELODIMALS_SIMON_MAX_LENGTH=999` cannot leave a three-year-old
 * facing an impossible sequence.
 */
export const NUMERIC_RANGES = {
  simonStartLength: [1, 5],
  simonMaxLength: [2, 16],
  simonDemoNoteMs: [200, 2000],
  whoSoundsRounds: [1, 20],
  whoSoundsChoices: [2, 6],
  countMax: [1, 20],
  countRounds: [1, 20],
  challengeMin: [1, 20],
  challengeSpan: [2, 20],
  gatePassMinutes: [0, 120],
  interstitialPerSession: [0, 5],
  interstitialPerDay: [0, 10],
  minGapMinutes: [1, 120],
} as const satisfies Record<string, readonly [number, number]>;

export type NumericKey = keyof typeof NUMERIC_RANGES;

/** Code defaults: the app's historical behavior, with a name. */
export function builtinAppConfig(): AppConfig {
  return {
    difficulty: "normal",
    games: { ...DIFFICULTY_TABLE.normal },
    narration: { enabled: true },
    parental: { challengeMin: 3, challengeSpan: 6, gatePassMinutes: 0 },
    // Children's regime §6: 1 per session, 2 per day, 10 minutes of separation.
    adCaps: { interstitialPerSession: 1, interstitialPerDay: 2, minGapMinutes: 10 },
  };
}

function clampNumber(key: NumericKey, value: number): number {
  const [min, max] = NUMERIC_RANGES[key];
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Valid number within range, or `undefined` (meaning "change nothing"). */
export function parseNumber(key: NumericKey, raw: unknown): number | undefined {
  const value = typeof raw === "string" ? Number(raw.trim()) : raw;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  return clampNumber(key, value);
}

function parseBool(raw: unknown): boolean | undefined {
  if (typeof raw === "boolean") {
    return raw;
  }
  if (typeof raw !== "string") {
    return undefined;
  }
  const s = raw.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(s)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(s)) {
    return false;
  }
  return undefined;
}

/** Is it a plain object? Predicate, not assertion: `JSON.parse` can return anything. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isDifficultyPreset(value: unknown): value is DifficultyPreset {
  return typeof value === "string" && (DIFFICULTY_PRESETS as readonly string[]).includes(value);
}

/**
 * Layer 2 — platform. The same keys as the adult level, in their env form
 * (`VITE_MELODIMALS_*`), so there are not two vocabularies for the same
 * control (a gotcha inherited from the kit).
 */
export function applyPlatformEnv(config: AppConfig, env: Record<string, string | undefined>): void {
  const difficulty = env.VITE_MELODIMALS_DIFFICULTY?.trim().toLowerCase();
  if (isDifficultyPreset(difficulty)) {
    config.difficulty = difficulty;
    config.games = { ...DIFFICULTY_TABLE[difficulty] };
  }

  const numeric: [NumericKey, string, (value: number) => void][] = [
    [
      "simonStartLength",
      "VITE_MELODIMALS_SIMON_START_LENGTH",
      (v) => {
        config.games.simonStartLength = v;
      },
    ],
    [
      "simonMaxLength",
      "VITE_MELODIMALS_SIMON_MAX_LENGTH",
      (v) => {
        config.games.simonMaxLength = v;
      },
    ],
    [
      "simonDemoNoteMs",
      "VITE_MELODIMALS_SIMON_DEMO_NOTE_MS",
      (v) => {
        config.games.simonDemoNoteMs = v;
      },
    ],
    [
      "whoSoundsRounds",
      "VITE_MELODIMALS_WHO_SOUNDS_ROUNDS",
      (v) => {
        config.games.whoSoundsRounds = v;
      },
    ],
    [
      "whoSoundsChoices",
      "VITE_MELODIMALS_WHO_SOUNDS_CHOICES",
      (v) => {
        config.games.whoSoundsChoices = v;
      },
    ],
    [
      "countMax",
      "VITE_MELODIMALS_COUNT_MAX",
      (v) => {
        config.games.countMax = v;
      },
    ],
    [
      "countRounds",
      "VITE_MELODIMALS_COUNT_ROUNDS",
      (v) => {
        config.games.countRounds = v;
      },
    ],
    [
      "challengeMin",
      "VITE_MELODIMALS_GATE_CHALLENGE_MIN",
      (v) => {
        config.parental.challengeMin = v;
      },
    ],
    [
      "challengeSpan",
      "VITE_MELODIMALS_GATE_CHALLENGE_SPAN",
      (v) => {
        config.parental.challengeSpan = v;
      },
    ],
    [
      "gatePassMinutes",
      "VITE_MELODIMALS_GATE_PASS_MINUTES",
      (v) => {
        config.parental.gatePassMinutes = v;
      },
    ],
    [
      "interstitialPerSession",
      "VITE_ADS_INTERSTITIAL_PER_SESSION",
      (v) => {
        config.adCaps.interstitialPerSession = v;
      },
    ],
    [
      "interstitialPerDay",
      "VITE_ADS_INTERSTITIAL_PER_DAY",
      (v) => {
        config.adCaps.interstitialPerDay = v;
      },
    ],
    [
      "minGapMinutes",
      "VITE_ADS_MIN_GAP_MINUTES",
      (v) => {
        config.adCaps.minGapMinutes = v;
      },
    ],
  ];
  for (const [key, name, assign] of numeric) {
    const value = parseNumber(key, env[name]);
    if (value !== undefined) {
      assign(value);
    }
  }

  const narration = parseBool(env.VITE_MELODIMALS_NARRATION);
  if (narration !== undefined) {
    config.narration.enabled = narration;
  }
}

/**
 * Layer 3 — the adult. Only the controls that make sense behind the parental
 * gate; fine-tuning controls (demo rhythm, gate challenge) stay at the
 * platform level on purpose.
 */
export function applyParentSettings(config: AppConfig, settings: ParentSettings): void {
  if (isDifficultyPreset(settings.difficulty)) {
    config.difficulty = settings.difficulty;
    config.games = { ...DIFFICULTY_TABLE[settings.difficulty] };
  }
  const narration = parseBool(settings.narrationEnabled);
  if (narration !== undefined) {
    config.narration.enabled = narration;
  }
  const gatePass = parseNumber("gatePassMinutes", settings.gatePassMinutes);
  if (gatePass !== undefined) {
    config.parental.gatePassMinutes = gatePass;
  }
  // Ads: the adult can ONLY harden. Asking for more than the platform cap is
  // not a user error to warn about — it simply does not increase.
  const perDay = parseNumber("interstitialPerDay", settings.interstitialPerDay);
  if (perDay !== undefined) {
    config.adCaps.interstitialPerDay = Math.min(config.adCaps.interstitialPerDay, perDay);
  }
  const perSession = parseNumber("interstitialPerSession", settings.interstitialPerSession);
  if (perSession !== undefined) {
    config.adCaps.interstitialPerSession = Math.min(
      config.adCaps.interstitialPerSession,
      perSession,
    );
  }
}

/**
 * Consistency between keys. Layered resolution can leave impossible
 * combinations (a `simonMaxLength` below `simonStartLength` makes the game
 * won before it starts): these are fixed here, always toward the playable
 * side.
 */
export function reconcileAppConfig(config: AppConfig): void {
  config.games.simonMaxLength = Math.max(
    config.games.simonMaxLength,
    config.games.simonStartLength,
  );
  // A session cap above the daily one would be unreachable (and would give a
  // misleading gate `reason`). Fix it by LOWERING the session cap, never by raising
  // the daily one: for ads, reconciliation must also harden — otherwise,
  // asking for "zero per day" would end up allowing one per session.
  config.adCaps.interstitialPerSession = Math.min(
    config.adCaps.interstitialPerSession,
    config.adCaps.interstitialPerDay,
  );
}

/** Complete resolution: defaults → platform → adult → reconciliation. */
export function resolveAppConfig(
  env: Record<string, string | undefined> = {},
  settings: ParentSettings = {},
): AppConfig {
  const config = builtinAppConfig();
  applyPlatformEnv(config, env);
  applyParentSettings(config, settings);
  reconcileAppConfig(config);
  return config;
}

/** `localStorage` key where the adult settings live (per device). */
export const PARENT_SETTINGS_KEY = "parent-config.v1";

/**
 * Reads the adult settings from `localStorage`. Any garbage (broken JSON,
 * a key from another version, a value of another type) returns EMPTY settings: it falls back to
 * the default, never halfway.
 */
export function readParentSettings(storage: Pick<Storage, "getItem"> | undefined): ParentSettings {
  try {
    const raw = storage?.getItem(PARENT_SETTINGS_KEY);
    if (!raw) {
      return {};
    }
    const record: unknown = JSON.parse(raw);
    if (!isRecord(record)) {
      return {};
    }
    const settings: ParentSettings = {};
    if (isDifficultyPreset(record.difficulty)) {
      settings.difficulty = record.difficulty;
    }
    const narration = parseBool(record.narrationEnabled);
    if (narration !== undefined) {
      settings.narrationEnabled = narration;
    }
    for (const key of [
      "gatePassMinutes",
      "interstitialPerDay",
      "interstitialPerSession",
    ] as const) {
      const value = parseNumber(key, record[key]);
      if (value !== undefined) {
        settings[key] = value;
      }
    }
    return settings;
  } catch {
    // Storage blocked (private mode) or corrupted JSON: defaults.
    return {};
  }
}

/** Persists the adult settings. Never throws: without storage it stays in memory. */
export function writeParentSettings(
  storage: Pick<Storage, "setItem"> | undefined,
  settings: ParentSettings,
): void {
  try {
    storage?.setItem(PARENT_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Like the rest of the app: the settings live only in this session.
  }
}
